import React, { useState } from "react"

import { makeStyles } from "makeStyles"
import { Table, TableBody, TableContainer, Paper, Box, TableRow, TableCell } from "@mui/material"
import { useSnackbar } from "notistack"

import TableHead from "./TableHead"
import ShoppingListLineItem from "./ShoppingListLineItem"

import mergeConfiguredLineItems from "@secureo/common/utils/commerceTools/mergeConfiguredLineItems"
import { getLineItemTaxRate } from "@secureo/common/utils/tax"
import { netToGross } from "@secureo/common/utils/tax"
import { getUpdatedConfigurationQuantity } from "containers/CartPage/LineItem"
import {
	addShoppingListLineItemsToCart,
	removeShoppingListLineItem,
	changeShoppingListLineItemQuantity,
} from "@secureo/common/context/AppContext/actions"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import { ShoppingListLineItem as ShoppingListLineItemInterface } from "@secureo/common/typings/ShoppingList"
import {
	TaxedItemPrice,
	ChangeShoppingListLineItemQuantity,
	CustomFieldsType,
} from "@secureo/common/typings/generated/commercetools-graphql"
import { MergedConfiguredLineItem, LineItem } from "@secureo/common/typings/Cart"
import { AppContextState } from "@secureo/common/context/AppContext/AppContext"

export const formatShoppingListLineItemToCartLineItem = (
	shoppingListLineItem: ShoppingListLineItemInterface,
	shippingDestinationCountryCode: CountryCode,
) => {
	const {
		variant: { shippingOriginationCountryCodesByDestinationCountryCode, pricesByCountryCode },
		quantity,
	} = shoppingListLineItem

	const shippingOriginationCountryCode =
		shippingOriginationCountryCodesByDestinationCountryCode?.[shippingDestinationCountryCode] ??
		"DE"
	const { taxRate } = getLineItemTaxRate(
		shippingOriginationCountryCode,
		shippingDestinationCountryCode,
	)

	const singleNetPriceCents = pricesByCountryCode?.[shippingDestinationCountryCode] ?? 0
	const singleGrossPriceCents = Math.round(netToGross(singleNetPriceCents, taxRate))

	const totalNetPriceCents = singleNetPriceCents * quantity
	const totalGrossPriceCents = singleGrossPriceCents * quantity
	const taxedPrice = {
		totalNet: {
			centAmount: totalNetPriceCents,
		},
		totalGross: {
			centAmount: totalGrossPriceCents,
		},
	} as any as TaxedItemPrice

	const cartLineItem: LineItem & { custom: CustomFieldsType } = {
		...shoppingListLineItem,
		taxedPrice,
		taxRate: {
			type: "lineItem",
			rate: taxRate,
			isCompanyTaxRate: false,
			includedInPrice: false,
			shippingOriginationCountryCode,
			shippingDestinationCountryCode,
		},
		isDiscounted: false,
		totalDiscountGrossCents: 0,
		totalUnDiscountedPriceGrossCents: totalGrossPriceCents,
		includedDiscounts: [],
	}

	return cartLineItem
}

const useStyles = makeStyles()((theme) => ({
	root: {
		width: "100%",
	},
	paper: {
		width: "100%",
		marginBottom: theme.spacing(2),
		boxShadow: "none",
	},
	table: {
		minWidth: 750,
	},
	visuallyHidden: {
		border: 0,
		clip: "rect(0 0 0 0)",
		height: 1,
		margin: -1,
		overflow: "hidden",
		padding: 0,
		position: "absolute",
		top: 20,
		width: 1,
	},
}))

const ShoppingList = ({ appContextState }: Props) => {
	const { accessToken, countryCode, shoppingList, dispatch } = appContextState
	const { classes } = useStyles()
	const { enqueueSnackbar } = useSnackbar()
	const [isLoading, setIsLoading] = useState(false)
	const [selectedLineItemIds, setSelectedLineItemIds] = useState([] as string[])
	const toggleIsSelected = (lineItemId: string) => {
		const hasBeenSelected = selectedLineItemIds.includes(lineItemId)

		setSelectedLineItemIds(
			hasBeenSelected
				? selectedLineItemIds.filter((id) => id !== lineItemId)
				: [...selectedLineItemIds, lineItemId],
		)
	}

	const { lineItems: shoppingListLineItems = [] } = shoppingList ?? {}
	const lineItems = shoppingListLineItems.map((shoppingListLineItem) =>
		formatShoppingListLineItemToCartLineItem(shoppingListLineItem, countryCode),
	)
	const mergedConfiguredLineItems = mergeConfiguredLineItems(lineItems)

	const changeQuantity = async (
		lineItem: MergedConfiguredLineItem,
		newConfiguredLineItemQuantity: number,
	) => {
		const { id, quantity: prevConfiguredLineItemQuantity, configurations = [] } = lineItem
		const hasConfigurations = (configurations?.length ?? 0) > 0

		const updateActions: ChangeShoppingListLineItemQuantity[] = [
			{
				lineItemId: id,
				quantity: newConfiguredLineItemQuantity,
			},
		]

		if (hasConfigurations) {
			configurations.forEach((configuration) => {
				const { id, quantity: prevConfigurationQuantity } = configuration

				const updateAction: ChangeShoppingListLineItemQuantity = {
					lineItemId: id,
					quantity: getUpdatedConfigurationQuantity(
						prevConfiguredLineItemQuantity,
						newConfiguredLineItemQuantity,
						prevConfigurationQuantity,
					),
				}

				updateActions.push(updateAction)
			})
		}

		setIsLoading(true)
		await changeShoppingListLineItemQuantity(
			accessToken,
			dispatch,
			shoppingList,
			updateActions,
			"EUR",
		)
		setIsLoading(false)
	}

	const remove = async (lineItem: MergedConfiguredLineItem, showSnackbar = true) => {
		const { id, configurations = [] } = lineItem
		const hasConfigurations = (configurations?.length ?? 0) > 0

		setIsLoading(true)
		const lineItemIdsToRemove = [id]
		if (hasConfigurations) {
			configurations.forEach((configuration) => {
				const { id } = configuration

				lineItemIdsToRemove.push(id)
			})
		}

		await removeShoppingListLineItem(
			accessToken,
			dispatch,
			shoppingList,
			lineItemIdsToRemove,
			"EUR",
		)
		setIsLoading(false)
		if (showSnackbar) enqueueSnackbar("Von der Wunschliste entfernt")
	}

	const addToCart = async (lineItem: MergedConfiguredLineItem) => {
		setIsLoading(true)
		await addShoppingListLineItemsToCart(appContextState, [lineItem])
		enqueueSnackbar("In den Warenkorb verschoben", {
			variant: "success",
		})
		remove(lineItem, false)
	}

	const toggleSelectAllLineItems = () => {
		const hasSelectedAllLineItemsPreviously =
			selectedLineItemIds.length === mergedConfiguredLineItems.length

		setSelectedLineItemIds(
			!hasSelectedAllLineItemsPreviously ? mergedConfiguredLineItems.map(({ id }) => id) : [],
		)
	}

	const removeSelectedLineItemsFromShoppingList = async (showSnackbar = true) => {
		setIsLoading(true)
		const selectedLineItems = mergedConfiguredLineItems.filter(({ id }) =>
			selectedLineItemIds.includes(id),
		)

		const lineItemIdsToRemove: string[] = []

		for (const lineItem of selectedLineItems) {
			const { id, configurations = [] } = lineItem
			const hasConfigurations = (configurations?.length ?? 0) > 0

			lineItemIdsToRemove.push(id)
			if (hasConfigurations) {
				configurations.forEach((configuration) => {
					const { id } = configuration

					lineItemIdsToRemove.push(id)
				})
			}
		}

		await removeShoppingListLineItem(
			accessToken,
			dispatch,
			shoppingList,
			lineItemIdsToRemove,
			"EUR",
		)

		setIsLoading(false)
		if (showSnackbar) enqueueSnackbar("Von der Wunschliste entfernt")
	}

	const addSelectedLineItemsToCart = async () => {
		setIsLoading(true)

		const selectedLineItems = mergedConfiguredLineItems.filter(({ id }) =>
			selectedLineItemIds.includes(id),
		)

		await addShoppingListLineItemsToCart(appContextState, selectedLineItems)
		await removeSelectedLineItemsFromShoppingList(false)
		enqueueSnackbar("In den Warenkorb verschoben", {
			variant: "success",
		})
	}

	return (
		<Box my={5} pb={5}>
			<div className={classes.root}>
				<Paper className={classes.paper}>
					<TableContainer>
						<Table
							className={classes.table}
							aria-labelledby="tableTitle"
							aria-label="enhanced table"
						>
							<TableHead
								classes={classes}
								numberOfSelectedLineItems={selectedLineItemIds.length}
								numberOfLineItems={mergedConfiguredLineItems.length}
								toggleSelectAllLineItems={toggleSelectAllLineItems}
								addSelectedLineItemsToCart={addSelectedLineItemsToCart}
								removeSelectedLineItemsFromShoppingList={
									removeSelectedLineItemsFromShoppingList
								}
							/>

							<TableBody>
								{mergedConfiguredLineItems.length > 0 ? (
									mergedConfiguredLineItems.map((lineItem) => {
										const isSelected = selectedLineItemIds.includes(lineItem.id)
										return (
											<ShoppingListLineItem
												isLoading={isLoading}
												key={lineItem.id}
												lineItem={lineItem}
												isSelected={isSelected}
												toggleIsSelected={toggleIsSelected}
												changeQuantity={changeQuantity}
												addToCart={addToCart}
												remove={remove}
												countryCode={countryCode}
											/>
										)
									})
								) : (
									<TableRow>
										<TableCell>
											<span>-</span>
										</TableCell>
										<TableCell>
											<p>Noch keine Artikel in Ihrer Wunschliste</p>
										</TableCell>
										<TableCell>
											<span></span>
										</TableCell>
										<TableCell>
											<span></span>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</div>
		</Box>
	)
}

interface Props {
	appContextState: AppContextState
}

export default ShoppingList
